import { createSlice, PayloadAction } from '@reduxjs/toolkit'

export type GridSize = 'small' | 'medium' | 'large'

interface UiState {
  sidebarOpen: boolean
  selectedImages: string[]
  modalImage: string | null
  modalImageList: string[]  // List of image IDs for modal navigation
  filterPanelOpen: boolean
  mobileMenuOpen: boolean
  gridSize: GridSize
}

const initialState: UiState = {
  sidebarOpen: true,
  selectedImages: [],
  modalImage: null,
  modalImageList: [],
  filterPanelOpen: false,
  mobileMenuOpen: false,
  gridSize: 'medium',
}

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    toggleSidebar: (state) => {
      state.sidebarOpen = !state.sidebarOpen
    },
    setSidebarOpen: (state, action: PayloadAction<boolean>) => {
      state.sidebarOpen = action.payload
    },
    selectImage: (state, action: PayloadAction<string>) => {
      if (!state.selectedImages.includes(action.payload)) {
        state.selectedImages.push(action.payload)
      }
    },
    deselectImage: (state, action: PayloadAction<string>) => {
      state.selectedImages = state.selectedImages.filter(id => id !== action.payload)
    },
    toggleImageSelection: (state, action: PayloadAction<string>) => {
      const index = state.selectedImages.indexOf(action.payload)
      if (index === -1) {
        state.selectedImages.push(action.payload)
      } else {
        state.selectedImages.splice(index, 1)
      }
    },
    selectRange: (state, action: PayloadAction<string[]>) => {
      state.selectedImages = [...new Set([...state.selectedImages, ...action.payload])]
    },
    clearSelection: (state) => {
      state.selectedImages = []
    },
    openImageModal: (state, action: PayloadAction<{ masterId: string; imageList?: string[] }>) => {
      state.modalImage = action.payload.masterId
      if (action.payload.imageList) {
        state.modalImageList = action.payload.imageList
      }
    },
    closeImageModal: (state) => {
      state.modalImage = null
    },
    navigateModal: (state, action: PayloadAction<'prev' | 'next'>) => {
      if (!state.modalImage || state.modalImageList.length === 0) return
      const currentIndex = state.modalImageList.indexOf(state.modalImage)
      if (currentIndex === -1) return

      if (action.payload === 'prev' && currentIndex > 0) {
        state.modalImage = state.modalImageList[currentIndex - 1]
      } else if (action.payload === 'next' && currentIndex < state.modalImageList.length - 1) {
        state.modalImage = state.modalImageList[currentIndex + 1]
      }
    },
    toggleFilterPanel: (state) => {
      state.filterPanelOpen = !state.filterPanelOpen
    },
    setFilterPanelOpen: (state, action: PayloadAction<boolean>) => {
      state.filterPanelOpen = action.payload
    },
    toggleMobileMenu: (state) => {
      state.mobileMenuOpen = !state.mobileMenuOpen
    },
    setMobileMenuOpen: (state, action: PayloadAction<boolean>) => {
      state.mobileMenuOpen = action.payload
    },
    cycleGridSize: (state) => {
      const sizes: GridSize[] = ['small', 'medium', 'large']
      const currentIndex = sizes.indexOf(state.gridSize)
      state.gridSize = sizes[(currentIndex + 1) % sizes.length]
    },
    setGridSize: (state, action: PayloadAction<GridSize>) => {
      state.gridSize = action.payload
    },
  },
})

export const {
  toggleSidebar,
  setSidebarOpen,
  selectImage,
  deselectImage,
  toggleImageSelection,
  selectRange,
  clearSelection,
  openImageModal,
  closeImageModal,
  navigateModal,
  toggleFilterPanel,
  setFilterPanelOpen,
  toggleMobileMenu,
  setMobileMenuOpen,
  cycleGridSize,
  setGridSize,
} = uiSlice.actions

export default uiSlice.reducer
